import React from "react";
import Box from "@mui/material/Box";
import {CmCollectionType} from "../../types";
import CCOComponentWrapper from "../../fundamentals/CCOComponentWrapper";
import CCOHeadline from "../../fundamentals/CCOHeadline";
import CCOContainer from "../../fundamentals/CCOContainer";
import useWindowSize from "../../../utility/useWindowSize";
import PlacementItem from "../../layouts/body/PlacementItem";
import C092LinkedImage, {LinkedImageItem} from "./C092LinkedImage";
import {ParentComponentProvider} from "../../../context/ParentComponentContext";

import type {CAEGlobalSettings} from "../../../types/CAEGlobalSettings";

export interface Props {
    cmCollection: CmCollectionType;
    CAEGlobalSettings?: CAEGlobalSettings;
}

type CollectionItem = NonNullable<NonNullable<CmCollectionType["items"]>[number]>;

function isCollectionItem(item: CollectionItem | null | undefined): item is CollectionItem {
    return item != null;
}

function isTeaserWithPicture(item: CollectionItem): item is CollectionItem & LinkedImageItem & { id: string } {
    return "type" in item && item.type === "SOSTeaser" && "picture" in item && item.picture != null;
}

function getGridTemplateColumns(columns: number, isSingleItem: boolean) {
    return isSingleItem ? "minmax(0, 400px)" : `repeat(${columns}, max-content)`;
}

function C092_3ColGrid({cmCollection, CAEGlobalSettings}: Props) {
    const {id, teaserTitle, teaserText} = cmCollection;
    const {greaterThanWindowLarge, isWindowMedium} = useWindowSize();

    const items = (cmCollection.items ?? []).filter(isCollectionItem);
    const itemCount = items.length;
    const maxColumns = greaterThanWindowLarge ? 3 : isWindowMedium ? 2 : 1;
    const columns = Math.min(maxColumns, itemCount || 1);
    const isSingleItem = itemCount === 1;

    function renderItem(item: CollectionItem) {
        if ("type" in item && item.type === "CMPicture") {
            return <C092LinkedImage hasTile={!isSingleItem} item={item}/>;
        }
        if (isTeaserWithPicture(item)) {
            return (
                <ParentComponentProvider id={item.id}>
                    <C092LinkedImage hasTile={!isSingleItem} item={item}/>
                </ParentComponentProvider>
            );
        }
        return (
            <PlacementItem
                item={item}
                CAEGlobalSettings={CAEGlobalSettings ?? ({} as CAEGlobalSettings)}
                csrfToken=""
            />
        );
    }

    return (
        <CCOComponentWrapper>
            <Box
                sx={{
                    backgroundColor: "grey.100",
                    px: "16px",
                    py: {xs: "24px", md: "48px"},
                }}
            >
                <CCOHeadline
                    metadataId={id}
                    id={id}
                    type="componenthead"
                    headline={teaserTitle}
                    propertyName="properties.teaserTitle"
                    subtextPropertyName="properties.teaserText"
                    subtext={teaserText?.textAsTree}
                />
                {
                    itemCount > 0 && (
                        <CCOContainer width="large">
                            <Box
                                sx={{
                                    display: "grid",
                                    gap: "24px",
                                    gridTemplateColumns: getGridTemplateColumns(columns, isSingleItem),
                                    justifyContent: "center",
                                }}
                            >
                                {items.map((item, index) => (
                                    <div key={`${id}-${index}`}>
                                        {renderItem(item)}
                                    </div>
                                ))}
                            </Box>
                        </CCOContainer>
                    )
                }
            </Box>
        </CCOComponentWrapper>
    );
}

export default C092_3ColGrid;
